import { useCallback, useEffect, useState } from "react";
import {
  listCountryDocumentsAction,
  getCountryDocumentByIdAction,
  deleteCountryDocumentAction,
  updateCountryDocumentStatusAction
} from "../../Actions/CountryDocumentActions";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import { ITEMS_PER_PAGE_OPTIONS } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditCountryDocument from "./AddEditCountry";
import DataTable from "../../components/DataTable/DataTable";

const CountryDocument = () => {

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [filterValues, setFilterValues] = useState({
    search: "",
    is_supported: "all",
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);

  const handleAdd = () => {
    setEditData(null);
    setOpenModal(true);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);

    try {
      const res = await listCountryDocumentsAction(
        currentPage,
        selectedRowsPerPage,
        searchValue,
        "",
        "",
        filterValues.is_supported
      );
      // const filteredData = (res?.data?.data || []).filter(
      //   (item) => item.is_supported === true
      // );

      setData(res?.data?.data || []);
      setTotalRecords(res?.data?.meta?.total || 0);

    } catch (error) {

      setSnackbar({
        open: true,
        message: error?.response?.data?.message || "Error fetching data",
        severity: "error",
      });
    }

    setLoading(false);
  }, [currentPage, selectedRowsPerPage, searchValue, filterValues.is_supported,]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (row) => {
    try {
      const payload = {
        changedFields: JSON.stringify(["status"]),
        comparisonData: JSON.stringify({
          country_document: {
            status: {
              old: "Active",
              new: "Deleted",
            },
            country: row.country?.name || "",
            document_type: row.documentType?.name || "",
          },
        }),
      };

      await deleteCountryDocumentAction(row.id, payload);

      setSnackbar({
        open: true,
        message: "Country document disabled successfully",
        severity: "success",
      });

      fetchData();
    } catch (error) {
      const status = error?.response?.status;

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Something went wrong",
        severity: status >= 500 ? "error" : "info",
      });
    }
  };

  const handleEdit = async (id) => {
    try {
      const res = await getCountryDocumentByIdAction(id);

      setEditData(res?.data || res);
      setOpenModal(true);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Failed to fetch details",
        severity: "error",
      });
    }
  };

  const handleStatusToggle = async (row) => {
    try {
      const payload = {
        is_supported: !row.is_supported,
        changedFields: JSON.stringify(["is_supported"]),
        comparisonData: JSON.stringify({
          country_document: {
            is_supported: {
              old: row.is_supported,
              new: !row.is_supported,
            },
            country: row.country?.name || "",
            document_type: row.documentType?.name || "",
          },
        }),
      };

      await updateCountryDocumentStatusAction(row.id, payload);

      setSnackbar({
        open: true,
        message: `Country document ${!row.is_supported ? "enabled" : "disabled"
          } successfully`,
        severity: "success",
      });

      fetchData();
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
    }
  };

  const columns = [
    {
      field: "country",
      headerName: "Country",
      flex: 1,
      renderCell: ({ row }) => row?.country?.name || "-",
    },
    {
      field: "documentType",
      headerName: "Document",
      flex: 1,
      renderCell: ({ row }) => row?.documentType?.name || "-",
    },
    {
      field: "is_supported",
      headerName: "Supported",
      flex: 1,
      renderCell: ({ row }) => (row?.is_supported ? "Yes" : "No"),
    },
    {
      field: "is_double_sided",
      headerName: "Double",
      flex: 1,
      renderCell: ({ row }) => (row?.is_double_sided ? "Yes" : "No"),
    },
  ];

  return (
    <div>
      <BreadcrumbsComponent />

      <DataTable
        rows={data}
        columns={columns}
        statusField="is_supported"
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        getRowId={(row) => row.id}
        onPageChange={(page) => setCurrentPage(page)}
        onPageSizeChange={(size) => {
          setSelectedRowsPerPage(size);
          setCurrentPage(1);
        }}


        filterValues={filterValues}

        onFilterApply={(values) => {
          setFilterValues(values);
          setSearchInput(values.search || "");
          setSearchValue(values.search || "");
          setCurrentPage(1);
        }}

        onFilterReset={() => {
          setFilterValues({
            search: "",
            is_supported: "all",
          });

          setSearchInput("");
          setSearchValue("");
          setCurrentPage(1);
        }}

        filters={[
          {
            type: "search",
            placeholder: "Search country document...",
          },
          {
            type: "select",
            name: "is_supported",
            label: "Supported",
            options: [
              { label: "All", value: "all" },
              { label: "Supported", value: "true" },
              { label: "Unsupported", value: "false" },
            ],
          },
        ]}

        actions={{
          edit: true,
          delete: true,
          activate: true,
          deactivate: true,
        }}

        onAction={(action, row) => {
          if (action === "edit") handleEdit(row.id);
          if (action === "delete") handleDelete(row);
          if (action === "activate" || action === "deactivate") {
            handleStatusToggle(row);
          }
        }}

        bulkActions={{
          delete: false,
        }}

        hasCreatePermission={true}
        onAddClick={handleAdd}
      />

      {/* <button
        className="btn btn-primary mt-3"
        onClick={() => setOpenModal(true)}
      >
        <MyAddRoundedIcon /> ADD
      </button> */}

      {openModal && (
        <AddEditCountryDocument
          open={openModal}
          handleClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          editData={editData}
          setSnackbar={setSnackbar}
          refresh={fetchData}
        />
      )}

      <CustomSnackbar
        {...snackbar}
        onClose={() =>
          setSnackbar((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />
    </div>
  );
};

export default CountryDocument;